import React from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
} from '@mui/material';
import {
  BusinessOutlined as BusinessIcon,
  PersonOutlined as PersonIcon,
  SettingsOutlined as SettingsIcon,
  ExtensionOutlined as ExtensionIcon,
  NotificationsOutlined as NotificationsIcon,
  SecurityOutlined as SecurityIcon,
  HistoryOutlined as HistoryIcon,
  FileDownloadOutlined as FileDownloadIcon,
  PolicyOutlined as PolicyIcon,
  PrivacyTipOutlined as PrivacyTipIcon,
} from '@mui/icons-material';

const Settings = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const menuItems = [
    {
      key: 'company-data',
      label: t('settings.menu.companyData'),
      icon: <BusinessIcon />,
      path: '/settings/company-data',
      disabled: true,
    },
    {
      key: 'my-profile',
      label: t('settings.menu.myProfile'),
      icon: <PersonIcon />,
      path: '/settings/my-profile',
      disabled: false,
    },
    {
      key: 'general',
      label: t('settings.menu.generalSettings'),
      icon: <SettingsIcon />,
      path: '/settings/general',
      disabled: true,
    },
    {
      key: 'integrations',
      label: t('settings.menu.integrations'),
      icon: <ExtensionIcon />,
      path: '/settings/integrations',
      disabled: true,
    },
    {
      key: 'notifications',
      label: t('settings.menu.notificationPreferences'),
      icon: <NotificationsIcon />,
      path: '/settings/notifications',
      disabled: true,
    },
    {
      key: 'auth-security',
      label: t('settings.menu.authSecurity'),
      icon: <SecurityIcon />,
      path: '/settings/auth-security',
      disabled: true,
    },
    {
      key: 'activity-logs',
      label: t('settings.menu.activityLogs'),
      icon: <HistoryIcon />,
      path: '/settings/activity-logs',
      disabled: true,
    },
    {
      key: 'data-export',
      label: t('settings.menu.dataExport'),
      icon: <FileDownloadIcon />,
      path: '/settings/data-export',
      disabled: true,
    },
    {
      key: 'retention',
      label: t('settings.menu.retentionPolicies'),
      icon: <PolicyIcon />,
      path: '/settings/retention',
      disabled: true,
    },
    {
      key: 'privacy',
      label: t('settings.menu.lgpdPrivacy'),
      icon: <PrivacyTipIcon />,
      path: '/settings/privacy',
      disabled: true,
    },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', gap: 3 }}>
        {/* Sidebar Menu */}
        <Paper
          sx={{
            width: 280,
            flexShrink: 0,
            p: 2,
            height: 'fit-content',
          }}
        >
          <Typography
            variant="h6"
            sx={{
              px: 2,
              py: 1,
              mb: 2,
              fontWeight: 600,
            }}
          >
            {t('settings.title')}
          </Typography>
          <List component="nav" sx={{ py: 0 }}>
            {menuItems.map((item) => (
              <ListItemButton
                key={item.key}
                selected={isActive(item.path)}
                onClick={() => !item.disabled && navigate(item.path)}
                disabled={item.disabled}
                sx={{
                  borderRadius: 1,
                  mb: 0.5,
                  opacity: item.disabled ? 0.5 : 1,
                  cursor: item.disabled ? 'not-allowed' : 'pointer',
                  '&.Mui-selected': {
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    },
                    '& .MuiListItemIcon-root': {
                      color: 'primary.contrastText',
                    },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: isActive(item.path) ? 'inherit' : 'text.secondary',
                    minWidth: 40,
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    variant: 'body2',
                    fontWeight: isActive(item.path) ? 600 : 400,
                  }}
                />
              </ListItemButton>
            ))}
          </List>
        </Paper>

        {/* Content Area */}
        <Box sx={{ flex: 1 }}>
          <Outlet />
        </Box>
      </Box>
    </Container>
  );
};

export default Settings;
